import { ChangeEvent, ReactElement, ReactNode, useCallback } from 'react';
import * as yup from 'yup';

import { Autocomplete, AutocompleteRenderOptionState } from '@breathelife/mui';
import { IconName } from '@zinnia/form-engine-sdk';

import { Icon } from '../Icon/Icon';
import { StyledAutocompleteTextField } from './Styles';
import { AutocompleteOption } from './options';

type Props<T extends AutocompleteOption> = {
  options: T[];
  option: T | null;
  id?: string;
  onChange: (value: T | null) => void;
  label?: string;
  validationError?: yup.ValidationError;
  disableClearable?: boolean;
  onClear?: () => void;
  groupBy?: (option: T) => string;
  disabled?: boolean;
  required?: boolean;
  startIconName?: IconName;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => ReactNode;
  boldedBorder?: boolean;
  error?: boolean;
};

export function AutoComplete<T extends AutocompleteOption>({
  options,
  option,
  id,
  onChange,
  label,
  validationError,
  onClear,
  disableClearable = true,
  groupBy,
  required,
  disabled = false,
  renderOption,
  startIconName,
  boldedBorder,
  error,
}: Props<T>): ReactElement {
  const handleChange = useCallback(
    (_: ChangeEvent<any>, selectedOption: T | null) => {
      if (disableClearable && selectedOption === null) return;
      onChange(selectedOption);
    },
    [onChange],
  );

  const startAdornment = startIconName ? <Icon name={startIconName} size='20px' /> : undefined;

  return (
    <Autocomplete
      id={id ?? 'autocomplete-input'}
      options={options}
      value={option}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      disableClearable={disableClearable}
      groupBy={groupBy}
      disabled={disabled}
      onInputChange={(_event, _newValue, reason) => {
        if (reason === 'reset') {
          onClear && onClear();
        }
      }}
      renderOption={renderOption}
      renderInput={(inputProps) => (
        <StyledAutocompleteTextField
          {...{
            ...inputProps,
            InputProps: {
              ...inputProps.InputProps,
              startAdornment: startAdornment,
              error: !!validationError || error,
            },
          }}
          label={label}
          inputVariant='outlined'
          error={!!validationError || error}
          required={required}
          helperText={validationError?.message}
          boldedBorder={boldedBorder}
        />
      )}
    />
  );
}
